import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';

export interface MarketBreadthData {
  total_stocks: number;
  advancing_count: number;
  declining_count: number;
  flat_count: number;
  limit_up_count: number;
  limit_down_count: number;
  volume_total_billion: string;
  volume_chg_pct: string;
}

export interface SentimentIndicatorsData {
  money_making_score: string;
  sentiment_phase: string;
  sentiment_summary: string;
  bullish_ma20_ratio: string;
  bullish_ma60_ratio: string;
  volume_breakout_count: number;
}

export interface MarketCTMatrixResponse {
  status: 'fresh' | 'stale' | 'empty' | 'disconnected' | 'loading' | string;
  trade_date?: string;
  session_kind: string;
  market_breadth: MarketBreadthData;
  sentiment_indicators: SentimentIndicatorsData;
  meta?: {
    state: string;
    scan_completed_at?: string;
    coverage_ratio?: string;
    stale_reason?: string;
  };
}

export interface AShareCTMatrixCardProps {
  colorMode?: 'mainland' | 'international';
  tradeDate?: string;
  sessionKind?: 'DAY_CLOSE' | 'MIDDAY';
}

export const AShareCTMatrixCard: React.FC<AShareCTMatrixCardProps> = ({
  colorMode = 'mainland',
  tradeDate,
  sessionKind = 'DAY_CLOSE',
}) => {
  const [data, setData] = useState<MarketCTMatrixResponse | null>(null);
  const [fsmState, setFsmState] = useState<'loading' | 'fresh' | 'stale' | 'empty' | 'disconnected'>('loading');

  const fetchCTMatrix = useCallback(async () => {
    setFsmState('loading');
    try {
      const params: Record<string, string> = { session_kind: sessionKind };
      if (tradeDate) {
        params.trade_date = tradeDate;
      }
      const query = new URLSearchParams(params).toString();
      const res = await apiClient.get<MarketCTMatrixResponse>(`/api/v1/market/a-share/ct-matrix?${query}`);

      if (res && res.status === 'fresh') {
        setData(res);
        setFsmState('fresh');
      } else if (res && res.status === 'stale') {
        setData(res);
        setFsmState('stale');
      } else if (res && res.status === 'empty') {
        setData(res);
        setFsmState('empty');
      } else {
        setData(res);
        setFsmState(res?.status === 'disconnected' ? 'disconnected' : 'empty');
      }
    } catch (err) {
      setData(null);
      setFsmState('disconnected');
    }
  }, [tradeDate, sessionKind]);

  useEffect(() => {
    fetchCTMatrix();
  }, [fetchCTMatrix]);

  // Mainland mode: Red is Bullish (Up = --color-bearish), Green is Bearish (Down = --color-bullish)
  // International: Green is Up (--color-bullish), Red is Down (--color-bearish)
  const upColor = colorMode === 'mainland' ? 'var(--color-bearish)' : 'var(--color-bullish)';
  const downColor = colorMode === 'mainland' ? 'var(--color-bullish)' : 'var(--color-bearish)';

  if (fsmState === 'loading') {
    return (
      <div style={{
        padding: '16px 20px',
        marginBottom: '16px',
        borderRadius: '8px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '12px',
        color: 'var(--text-secondary)',
      }}>
        <span>正在加载全市场 CT 扫描矩阵与多空情绪温度...</span>
        <span style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--accent-cyan)' }}>
          ● LOADING
        </span>
      </div>
    );
  }

  if (fsmState === 'disconnected' || !data || fsmState === 'empty') {
    return (
      <div style={{
        padding: '16px 20px',
        marginBottom: '16px',
        borderRadius: '8px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            padding: '2px 8px',
            borderRadius: '4px',
            background: 'var(--badge-neutral-bg)',
            color: 'var(--text-secondary)',
            fontWeight: 600,
            fontSize: '11px',
          }}>
            市场 CT 扫描
          </span>
          <span style={{ color: 'var(--text-secondary)' }}>
            {fsmState === 'disconnected' ? '时序计算底座连接中断，暂无市场全景数据' : '当前交易时段暂无全市场扫描数据'}
          </span>
        </div>
        <span style={{
          fontFamily: 'var(--font-mono, monospace)',
          fontSize: '11px',
          color: fsmState === 'disconnected' ? 'var(--color-bearish)' : 'var(--text-secondary)',
        }}>
          ● {fsmState.toUpperCase()}
        </span>
      </div>
    );
  }

  const { market_breadth: b, sentiment_indicators: s } = data;
  const total = b.total_stocks || 1;
  const advPct = ((b.advancing_count / total) * 100).toFixed(1);
  const decPct = ((b.declining_count / total) * 100).toFixed(1);
  const flatPct = ((b.flat_count / total) * 100).toFixed(1);

  // Phase badge styles
  let phaseBg = 'var(--badge-info-bg)';
  let phaseColor = 'var(--accent-cyan)';
  if (s.sentiment_phase === 'EXPANSION') {
    phaseBg = 'var(--badge-bullish-bg)';
    phaseColor = upColor;
  } else if (s.sentiment_phase === 'CONTRACTION') {
    phaseBg = 'var(--badge-bearish-bg)';
    phaseColor = downColor;
  }

  return (
    <div style={{
      padding: '16px 20px',
      marginBottom: '16px',
      borderRadius: '8px',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-subtle)',
      boxShadow: 'var(--shadow-card)',
    }}>
      {/* Top row: Title, Sentiment Score & Phase */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid var(--border-subtle)',
        paddingBottom: '12px',
        marginBottom: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{
            fontSize: '13px',
            fontWeight: 700,
            color: 'var(--text-primary)',
            letterSpacing: '0.02em',
          }}>
            全市场 CT 全景透视
          </span>
          <span style={{
            fontSize: '10px',
            padding: '2px 6px',
            borderRadius: '3px',
            background: phaseBg,
            color: phaseColor,
            fontWeight: 700,
            fontFamily: 'var(--font-mono, monospace)',
          }}>
            {s.sentiment_phase}
          </span>
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            {s.sentiment_summary}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>赚钱效应温度:</span>
            <span style={{
              fontSize: '16px',
              fontWeight: 800,
              fontFamily: 'var(--font-mono, monospace)',
              color: phaseColor,
            }}>
              {s.money_making_score}
            </span>
            <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>/100</span>
          </div>

          <span style={{
            fontSize: '10px',
            padding: '2px 6px',
            borderRadius: '4px',
            background: 'var(--badge-bullish-bg)',
            color: 'var(--color-emerald)',
            fontWeight: 600,
            fontFamily: 'var(--font-mono, monospace)',
          }}>
            ● {data.status.toUpperCase()} ({data.session_kind})
          </span>
        </div>
      </div>

      {/* Middle Grid: Breadth Breakdown & Multi-Factor Ratios */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
        gap: '16px',
        alignItems: 'center',
      }}>
        {/* Metric 1: Advancing vs Declining distribution */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px' }}>
            <span style={{ color: upColor, fontWeight: 600 }}>涨 {b.advancing_count} ({advPct}%)</span>
            <span style={{ color: 'var(--text-secondary)' }}>平 {b.flat_count}</span>
            <span style={{ color: downColor, fontWeight: 600 }}>跌 {b.declining_count} ({decPct}%)</span>
          </div>
          {/* Stacked bar */}
          <div style={{
            height: '6px',
            borderRadius: '3px',
            background: 'var(--tag-inactive-bg)',
            display: 'flex',
            overflow: 'hidden',
          }}>
            <div style={{ width: `${advPct}%`, background: upColor, transition: 'width 0.3s' }} />
            <div style={{ width: `${flatPct}%`, background: 'var(--text-secondary)', transition: 'width 0.3s' }} />
            <div style={{ width: `${decPct}%`, background: downColor, transition: 'width 0.3s' }} />
          </div>
        </div>

        {/* Metric 2: Limit Up & Down */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-around',
          borderLeft: '1px solid var(--border-subtle)',
          paddingLeft: '12px',
        }}>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>涨停家数</div>
            <div style={{
              fontSize: '15px',
              fontWeight: 700,
              color: upColor,
              fontFamily: 'var(--font-mono, monospace)',
            }}>
              {b.limit_up_count}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>跌停家数</div>
            <div style={{
              fontSize: '15px',
              fontWeight: 700,
              color: downColor,
              fontFamily: 'var(--font-mono, monospace)',
            }}>
              {b.limit_down_count}
            </div>
          </div>
        </div>

        {/* Metric 3: Volume Turnover */}
        <div style={{
          borderLeft: '1px solid var(--border-subtle)',
          paddingLeft: '12px',
        }}>
          <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>全市场总成交额</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
            <span style={{
              fontSize: '15px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-mono, monospace)',
            }}>
              {b.volume_total_billion}
            </span>
            <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>亿元</span>
            {b.volume_chg_pct !== '0.00' && (
              <span style={{
                fontSize: '11px',
                fontWeight: 600,
                color: b.volume_chg_pct.startsWith('-') ? downColor : upColor,
                fontFamily: 'var(--font-mono, monospace)',
              }}>
                {b.volume_chg_pct}%
              </span>
            )}
          </div>
        </div>

        {/* Metric 4: Bullish MA Alignment & Volume Breakouts */}
        <div style={{
          borderLeft: '1px solid var(--border-subtle)',
          paddingLeft: '12px',
        }}>
          <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>站上MA20 / 放量突破</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{
              fontSize: '14px',
              fontWeight: 700,
              color: 'var(--accent-cyan)',
              fontFamily: 'var(--font-mono, monospace)',
            }}>
              {(Number(s.bullish_ma20_ratio || 0) * 100).toFixed(1)}%
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              倍量 <strong style={{ color: 'var(--text-primary)' }}>{s.volume_breakout_count}</strong> 家
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
